import { useState } from "react";
import { Check, Sprout } from "lucide-react";
import { EMS_SCHEMAS } from "../lib/config";
import { COPING_STYLES, coreNeed, needGroups, schemaScript } from "../lib/schemaFlip";
import { NEED_UNSURE } from "./NeedPicker";

export function SchemaRecognizer({ needs, suggested = [], reasons = {}, value, onChange }) {
  const domains = needGroups(needs).map((g) => coreNeed(g)?.domain).filter(Boolean);
  // "suggested" = AI picks only; "related" = schemas in the chosen needs' domains; "all" = all 18.
  const [view, setView] = useState(suggested.length ? "suggested" : needs.includes(NEED_UNSURE) || domains.length === 0 ? "all" : "related");
  const inNeed = (name) => domains.includes(EMS_SCHEMAS.find((s) => s.name === name)?.domain);
  const picked = (n) => value.some((v) => v.schema === n);
  const candidates = EMS_SCHEMAS.map((s) => s.name)
    .filter((n) => view === "all" || suggested.includes(n) || picked(n) || (view === "related" && inNeed(n)))
    .sort((a, b) => (suggested.includes(a) ? suggested.indexOf(a) : 99) - (suggested.includes(b) ? suggested.indexOf(b) : 99));
  const relatedExtra = EMS_SCHEMAS.filter((s) => inNeed(s.name) && !suggested.includes(s.name)).length;

  const toggle = (schema) =>
    onChange(value.some((v) => v.schema === schema) ? value.filter((v) => v.schema !== schema) : [...value, { schema, copingStyle: null }]);
  const setStyle = (schema, copingStyle) => onChange(value.map((v) => (v.schema === schema ? { ...v, copingStyle } : v)));

  return (
    <div className="flex flex-col gap-3 mt-4">
      {candidates.map((name, idx) => {
        const sc = schemaScript(name);
        const picked = value.find((v) => v.schema === name);
        return (
          <div key={name} className={`rounded-[20px] border transition-colors ${picked ? "border-t-brand bg-t-brand3" : "border-t-border bg-t-surface2"}`}>
            <button type="button" onClick={() => toggle(name)} data-testid={`recognize-${idx}`} className="w-full flex gap-3 p-4 text-left">
              <span className="flex-1 flex flex-col gap-1">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-t-on">{name}</span>
                  {suggested.includes(name) ? <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-t-brand text-t-onbrand">Suggested</span> : null}
                </span>
                {reasons[name] ? <span className="text-[13px] text-t-onbrand3 bg-t-brand3 rounded-xl px-3 py-2 not-italic" data-testid={`recognize-reason-${idx}`}>{reasons[name]}</span> : null}
                <span className="text-[15px] italic text-t-on2">“{sc?.script}”</span>
                <span className="text-[13px] text-t-brand font-medium">{picked ? "Sounds like me" : "Sounds like my inner voice?"}</span>
              </span>
              <span className={`shrink-0 w-7 h-7 rounded-full border-2 grid place-items-center ${picked ? "bg-t-brand border-t-brand text-t-onbrand" : "border-t-border2"}`}>
                {picked ? <Check size={15} strokeWidth={3} /> : null}
              </span>
            </button>
            {picked && sc ? (
              <div className="px-4 pb-4 flex flex-col gap-2">
                <p className="text-sm font-semibold text-t-on2">What did you do?</p>
                {COPING_STYLES.map((c) => {
                  const on = picked.copingStyle === c.key;
                  return (
                    <button key={c.key} type="button" onClick={() => setStyle(name, c.key)} data-testid={`style-${idx}-${c.key}`}
                      className={`text-left rounded-2xl border p-3 transition-colors ${on ? "bg-t-brand border-t-brand text-t-onbrand" : "bg-t-surface border-t-border text-t-on"}`}>
                      <span className="block font-semibold text-sm">{c.label}</span>
                      <span className={`block text-[13px] ${on ? "opacity-90" : "text-t-on2"}`}>{c.key === "healthy" ? `${sc.adaptive}: “${sc.adaptiveScript}”` : sc[c.key]}</span>
                    </button>
                  );
                })}
                {picked.copingStyle === "healthy" ? (
                  <div className="flex items-center gap-2 rounded-2xl bg-t-success text-t-onsuccess p-3 text-sm font-medium" data-testid={`healthy-win-${idx}`}>
                    <Sprout size={18} /> Healthy Adult win! Choosing your adaptive response grows your streak.
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      <div className="flex flex-col items-center gap-1">
        {view === "suggested" && relatedExtra > 0 ? (
          <button type="button" onClick={() => setView("related")} className="text-t-brand font-semibold py-2" data-testid="recognize-show-related">None of these fit? Show {relatedExtra} more linked to your needs</button>
        ) : null}
        {view !== "all" ? (
          <button type="button" onClick={() => setView("all")} className="text-t-muted text-sm font-semibold py-1" data-testid="recognize-show-all">Show all 18 schemas</button>
        ) : suggested.length ? (
          <button type="button" onClick={() => setView("suggested")} className="text-t-muted text-sm font-semibold py-1" data-testid="recognize-show-suggested">Show only suggested</button>
        ) : domains.length ? (
          <button type="button" onClick={() => setView("related")} className="text-t-muted text-sm font-semibold py-1" data-testid="recognize-show-related-only">Show only schemas linked to my needs</button>
        ) : null}
      </div>
    </div>
  );
}
